import React from "react";
import { Box, Card, Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";

// Standard Card Loading Skeleton
export const CardSkeleton = () => (
  <Card sx={{ p: 3, height: "100%" }}>
    <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1.5 }} />
    <Skeleton variant="rectangular" height={140} sx={{ borderRadius: "8px", mb: 2 }} />
    <Skeleton variant="text" width="40%" height={20} />
  </Card>
);

// High-Density Table Loading Skeleton
export interface TableSkeletonProps {
  columns?: number;
  rows?: number;
}

export const TableSkeleton = ({ columns = 5, rows = 5 }: TableSkeletonProps) => (
  <TableContainer sx={{ border: "1px solid #E2E8F0", borderRadius: "12px", bgcolor: "#fff" }}>
    <Table>
      <TableHead>
        <TableRow>
          {Array.from({ length: columns }).map((_, idx) => (
            <TableCell key={idx}>
              <Skeleton variant="text" width="70%" height={24} />
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rows }).map((_, rowIdx) => (
          <TableRow key={rowIdx}>
            {Array.from({ length: columns }).map((_, colIdx) => (
              <TableCell key={colIdx}>
                <Skeleton variant="text" width={colIdx === 0 ? "50%" : "85%"} height={20} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// Simplified Dashboard Performance Skeleton
export const DashboardSkeleton = () => (
  <Box display="flex" flexDirection="column" gap={3}>
    {/* Page Header Skeleton */}
    <Box>
      <Skeleton variant="text" width="30%" height={32} />
      <Skeleton variant="text" width="45%" height={20} />
    </Box>

    {/* KPI Grid Skeleton */}
    <Grid container spacing={3}>
      {Array.from({ length: 4 }).map((_, idx) => (
        <Grid item xs={12} sm={6} md={3} key={idx}>
          <Card sx={{ p: 2.5 }}>
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={36} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="30%" height={16} />
          </Card>
        </Grid>
      ))}
    </Grid>

    {/* Central Content Split Layout Skeletons */}
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card sx={{ p: 3 }}>
          <Skeleton variant="text" width="25%" height={24} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={260} sx={{ borderRadius: "8px" }} />
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card sx={{ p: 3 }}>
          <Skeleton variant="text" width="40%" height={24} sx={{ mb: 2 }} />
          <Box display="flex" flexDirection="column" gap={2}>
            {Array.from({ length: 4 }).map((_, idx) => (
              <Box key={idx} display="flex" alignItems="center" gap={2}>
                <Skeleton variant="circular" width={32} height={32} />
                <Box flex={1}>
                  <Skeleton variant="text" width="80%" height={18} />
                  <Skeleton variant="text" width="40%" height={14} />
                </Box>
              </Box>
            ))}
          </Box>
        </Card>
      </Grid>
    </Grid>
  </Box>
);
